import { useState, useEffect } from 'react'
import { colorDeEstatusProveedor } from '@/shared/lib/coloresDeEstatus'
import { useNavigate } from 'react-router-dom'
import { Search, Eye, Trash2, MoreVertical } from 'lucide-react'
import { apiService } from '@/services/api'
import { formatDate } from '@/shared/lib/dateUtils'
import { getSupplierStatusConfig } from '@/features/proveedores/lib/supplierStatusConfig'
import { Badge } from '@/shared/components/Badge'
import ConfirmActionModal from '@/shared/components/ConfirmActionModal'
import Toast from '@/shared/components/Toast'
import { useAuth } from '@/shared/hooks/useAuth'
import { userHasPermission } from '@/shared/lib/roleUtils'
import PermissionsDenyPage from '@/pages/configuracion/roles/permissionsDenyPage'

interface Supplier {
    id: string
    rfc: string
    legalName: string
    commercialName: string | null
    personType?: string
    vatType?: string
    serviceTypeId?: string
    country?: string
    state?: string
    city?: string
    municipality?: string
    suburb?: string
    postalCode?: string
    street?: string
    externalNumber?: string
    internalNumber?: string | null
    mainContactName: string
    mainContactPhone: string
    salesEmail?: string
    accountsPayableEmail?: string
    internalRequesterName?: string
    repseNumber?: string | null
    taxRegime?: string | null
    cfdiUse?: string | null
    taxEmail?: string | null
    taxPhone?: string | null
    origin: string
    status: string
    createdAt: string
    updatedAt: string
    hasAllDocumentsApproved?: boolean
    email?: string
    isActive?: boolean
}

interface SuppliersResponse {
    data: Supplier[]
    meta: {
        total: number
        page: number
        limit: number
        totalPages: number
    }
}

const BorradoresProveedores = () => {
    const { user } = useAuth()
    const navigate = useNavigate()
    const [searchTerm, setSearchTerm] = useState('')
    const [solicitudes, setSolicitudes] = useState<Supplier[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [showRejectModal, setShowRejectModal] = useState(false)
    const [actionSupplierId, setActionSupplierId] = useState<string | null>(null)
    const [actionLoading, setActionLoading] = useState(false)
    const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)
    const [openMenuId, setOpenMenuId] = useState<string | null>(null)
    const [_totalCount, setTotalCount] = useState(0)

    const fetchSolicitudes = async () => {
        try {
            setLoading(true)
            setError(null)
            const response = await apiService.request<SuppliersResponse>(
                '/admin/suppliers/admin-registered/pending'
            )
            setSolicitudes(response.data || [])
            setTotalCount(response.meta?.total || (response.data?.length || 0))
        } catch (errCrudo: unknown) {
          const err = errCrudo as {
            message?: string;
            statusCode?: number;
            status?: number;
            error?: string;
            response?: { status?: number; data?: { message?: string } };
          };
            console.error('Error al cargar borradores:', err)
            setError(err.message || 'Error al cargar los borradores')
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchSolicitudes()
    }, [])

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (openMenuId && !(event.target as Element).closest('.action-menu-container')) {
                setOpenMenuId(null)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [openMenuId])

    const handleReject = (supplierId: string) => {
        setActionSupplierId(supplierId)
        setShowRejectModal(true)
        setOpenMenuId(null)
    }

    const confirmReject = async () => {
        if (!actionSupplierId) return

        try {
            setActionLoading(true)
            await apiService.request(
                `/internal/suppliers/${actionSupplierId}/status`,
                {
                    method: 'PATCH',
                    body: JSON.stringify({ status: 'rejected' })
                }
            )
            setToast({ message: 'Proveedor rechazado exitosamente', type: 'success' })
            setShowRejectModal(false)
            setActionSupplierId(null)
            fetchSolicitudes() // Refrescar lista
        } catch (errCrudo: unknown) {
          const err = errCrudo as {
            message?: string;
            statusCode?: number;
            status?: number;
            error?: string;
            response?: { status?: number; data?: { message?: string } };
          };
            setToast({ message: err.message || 'Error al rechazar el proveedor', type: 'error' })
        } finally {
            setActionLoading(false)
        }
    }


    const filteredSolicitudes = solicitudes.filter(solicitud =>
        solicitud.legalName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        solicitud.salesEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        solicitud.rfc?.toLowerCase().includes(searchTerm.toLowerCase())
    )

  if (
    userHasPermission(
      "proveedores.administracion.proveedores.consultar",
      user?.rbac?.effectivePermissions || [],
    ) === false
  ) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <PermissionsDenyPage />
      </div>
    );
  }

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-6">
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">Borradores de solicitudes de proveedores</h1>
                    <p className="text-gray-600">Administra los borradores de solicitudes de alta de proveedores</p>
                </div>

                {/* Search Bar */}
                <div className="mb-6">
                    <div className="relative">
                        <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Buscar por proveedor, RFC o email..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>
                </div>

                {/* Loading State */}
                {loading && (
                    <div className="bg-white rounded-lg shadow p-12 text-center">
                        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        <p className="mt-4 text-gray-600">Cargando borradores...</p>
                    </div>
                )}

                {/* Error State */}
                {error && !loading && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <p className="text-red-800 text-sm">
                            <strong>Error:</strong> {error}
                        </p>
                        <button
                            onClick={fetchSolicitudes}
                            className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
                        >
                            Reintentar
                        </button>
                    </div>
                )}

                {/* Table */}
                {!loading && !error && (
                    <div className="bg-white rounded-lg overflow-hidden border border-[#E5E7EB] mb-[100px]">
                  <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-[#E5E7EB]">
                            <thead className="bg-[#FAF9FF]">
                                <tr>
                                    <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Proveedor</th>
                                    <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Email</th>
                                    <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Creado</th>
                                    <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Estatus</th>
                                    <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Acciones</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-[#E1E1E1]">
                                {filteredSolicitudes.map((solicitud) => (
                                    <tr key={solicitud.id} className="hover:bg-[#F4F7FB] transition-colors">
                                        <td className="px-6 py-4 cursor-pointer"
                                           onClick={() => {
                                                navigate(`/borradores/${solicitud.id}`)
                                                setOpenMenuId(null)
                                            }}
                                        >
                                            <div className="text-sm font-medium text-gray-900">{solicitud.legalName}</div>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-600 cursor-pointer"
                                            onClick={() => {
                                                navigate(`/borradores/${solicitud.id}`)
                                                setOpenMenuId(null)
                                            }}
                                        >{solicitud.email || solicitud.salesEmail}</td>
                                        <td className="px-6 py-4 text-sm text-gray-600 cursor-pointer"
                                            onClick={() => {
                                                navigate(`/borradores/${solicitud.id}`)
                                                setOpenMenuId(null)
                                            }}
                                        >{formatDate(solicitud.createdAt)}</td>
                                        <td className="px-6 py-4 cursor-pointer"
                                            onClick={() => {
                                                navigate(`/borradores/${solicitud.id}`)
                                                setOpenMenuId(null)
                                            }}
                                        >
                                            <Badge color={colorDeEstatusProveedor(solicitud.status)}>
                                                {getSupplierStatusConfig(solicitud.status).label}
                                            </Badge>
                                        </td>
                                        <td className="px-6 py-4 relative action-menu-container">
                                            <div className="relative">
                                                <button
                                                    onClick={() => setOpenMenuId(openMenuId === solicitud.id ? null : solicitud.id)}
                                                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-500"
                                                >
                                                    <MoreVertical className="w-5 h-5" />
                                                </button>

                                                {openMenuId === solicitud.id && (
                                                    <>
                                                        <div className="fixed inset-0 z-10" onClick={() => setOpenMenuId(null)} />
                                                        <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
                                                            <button
                                                                onClick={() => {
                                                                    navigate(`/borradores/${solicitud.id}`)
                                                                    setOpenMenuId(null)
                                                                }}
                                                                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                                            >
                                                                <Eye className="w-4 h-4" />
                                                                Ver
                                                            </button>

                                                            <button
                                                                onClick={() => handleReject(solicitud.id)}
                                                                className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                                Eliminar
                                                            </button>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                  </div>

                        {filteredSolicitudes.length === 0 && (
                            <div className="text-center py-12">
                                <p className="text-gray-500">No se encontraron borradores</p>
                            </div>
                        )}
                    </div>
                )}

                {/* Modals */}
                <ConfirmActionModal
                    isOpen={showRejectModal}
                    title="Rechazar Proveedor"
                    message="¿Estás seguro de que deseas rechazar este proveedor? Esta acción cambiará su estado a 'Rechazado'."
                    confirmText="Rechazar"
                    variante="peligro"
                    onConfirm={confirmReject}
                    onCancel={() => {
                        setShowRejectModal(false)
                        setActionSupplierId(null)
                    }}
                    isLoading={actionLoading}
                />

                {/* Toast */}
                {toast && (
                    <Toast
                        message={toast.message}
                        type={toast.type}
                        onClose={() => setToast(null)}
                    />
                )}
            </div>
        </div>
    )
}

export default BorradoresProveedores
